import axios, { AxiosError } from "axios";
import { Ref, ref } from "vue";
import localStorageService from "../services/utilities/local-storage-service";
import {
  CmsLink,
  CmsDocumentTypes,
  CmsTeamLevelData,
  CmsTeamLevelsAndMilestonesListItem,
  CmsMilestoneData,
} from "../services/cms/cms-type-definitions";
import { Status } from "../typings/shared/status";
import {
  PersonalPathwayProgressApiModel,
  TeamCompletedLevelsAndMilestonesApiModel,
  TeamMemberCompletedPathwayStepApiModel,
  TeamPathwayApiModel,
} from "../api-models";
import cms from "../services/cms/cms";
import useSelectTeamPathwayFromLevel from "./useSelectTeamPathwayFromLevel";
import useSelectPersonalPathwayForTeamMembers from "./useSelectPersonalPathwayForTeamMembers";
import isLiveEnvironment from "../services/utilities/environment";

export type CurrentPathwayIds = {
  teamPathway: {
    id: string;
    stepOneId: string;
    stepTwoId: string;
  };
  personalPathway: {
    id: string;
    stepOneId: string;
    stepTwoId: string;
  };
};

type TeamPathwayProgress = {
  teamPathway: TeamPathwayApiModel;
  userProgress: TeamMemberCompletedPathwayStepApiModel;
};

export type LevelCompleteStatus = "pending" | "success" | "error";

export type LevelCompleteResponse = {
  status: Ref<LevelCompleteStatus>;
  errorMessage: Ref<string>;
};

export type LevelType = "level" | "milestone";

export type LevelItem = {
  data: CmsTeamLevelData | CmsMilestoneData;
  id: string;
  uid: string;
  type: LevelType;
  number: number;
  status: Status;
  isOpen: boolean;
};

type TeamLeadLevelsReturn = {
  prepareLevelsAndMilestones: (
    levelsAndMilestonesList: CmsTeamLevelsAndMilestonesListItem[],
  ) => LevelItem[];
  setLevelsStatus: (
    levels: LevelItem[],
    levelsProgress: TeamCompletedLevelsAndMilestonesApiModel,
  ) => void;
  castAsLevel: (level: CmsTeamLevelData | CmsMilestoneData) => CmsTeamLevelData;
  setLevelLink: (level: LevelItem) => string;
  initiatePathwayFromLevel: (
    level: CmsTeamLevelData,
    teamId: string,
    deadline: string,
  ) => Promise<void>;
  getCurrentPathwayIds: (teamId?: string) => Promise<CurrentPathwayIds>;
  completeLevel: (level: LevelItem, teamId: string | undefined) => Promise<LevelCompleteResponse>;
};

const castAsLevel = (level: CmsTeamLevelData | CmsMilestoneData): CmsTeamLevelData => {
  return level as CmsTeamLevelData;
};

function itemIsTeamLevel(item: CmsTeamLevelData | CmsMilestoneData): boolean {
  if (!item) return false;
  return "level_type" in item;
}

const useTeamLeadLevels = (): TeamLeadLevelsReturn => {
  const prepareLevelsAndMilestones = (
    levelsAndMilestones: CmsTeamLevelsAndMilestonesListItem[],
  ): LevelItem[] => {
    const openLevelId = localStorageService.get<string>("teamLeadOpenLevelId");

    const filteredLevelsAndMilestones = levelsAndMilestones.filter((item) => {
      return !item?.in_testing || !isLiveEnvironment();
    });

    return filteredLevelsAndMilestones.map((item, index) => {
      const { data } = item.level_or_milestone;
      const type = itemIsTeamLevel(data) ? "level" : "milestone";
      const number =
        type === "level"
          ? (data as CmsTeamLevelData).level_number
          : (data as CmsMilestoneData).milestone_number;

      return {
        data,
        id: item.level_or_milestone.id,
        uid: item.level_or_milestone.uid,
        type,
        number,
        status: "uncompleted",
        isOpen: openLevelId === item.level_or_milestone.id,
      };
    });
  };

  const setLevelsStatus = (
    levels: LevelItem[],
    levelsProgress: TeamCompletedLevelsAndMilestonesApiModel,
  ) => {
    if (!levelsProgress.completedLevels.length) {
      levels[0].status = "active";
      return;
    }

    const isLevelCompleted = (levelId: string) =>
      levelsProgress.completedLevels.some((l) => l.prismicId === levelId) ||
      levelsProgress.completedMilestones.some((m) => m.prismicId === levelId);

    const firstUncompletedIndex = levels.findIndex((level) => !isLevelCompleted(level.id));

    levels.forEach((level, idx) => {
      if (idx === firstUncompletedIndex) {
        level.status = "active";
      } else {
        level.status = isLevelCompleted(level.id) ? "completed" : "uncompleted";
      }
    });
  };

  const setLevelLink = (level: LevelItem) => {
    const levelData = level.data as CmsTeamLevelData;
    const resolvedLink = cms.linkResolver(levelData.resource_link as CmsLink);
    const levelTitle = cms.asText(levelData.title).replace("&", "and");
    const levelNumber = levelData.level_number;
    const levelId = level.id;
    const levelUid = level.uid;
    return `${resolvedLink}?levelTitle=${levelTitle}&levelNumber=${levelNumber}&levelId=${levelId}&levelUid=${levelUid}`;
  };

  const initiatePathwayFromLevel = async (
    level: CmsTeamLevelData,
    teamId: string,
    deadline: string,
  ): Promise<void> => {
    if (level.pathway_type === "team" && level.pathway_to_initiate) {
      const response = await useSelectTeamPathwayFromLevel(
        level.pathway_to_initiate,
        teamId,
        deadline,
      );
      if (!response.success.value) {
        throw new Error("There was an error initiating the pathway");
      }
    } else if (level.pathway_type === "personal" && level.pathway_to_initiate) {
      const response = await useSelectPersonalPathwayForTeamMembers(
        level.pathway_to_initiate,
        teamId,
      );
      if (!response.success.value) {
        throw new Error("There was an error initiating the pathway");
      }
    }
  };

  const getCurrentPathwayIds = async (teamId?: string): Promise<CurrentPathwayIds> => {
    const currentPathways: CurrentPathwayIds = {
      teamPathway: { id: "", stepOneId: "", stepTwoId: "" },
      personalPathway: { id: "", stepOneId: "", stepTwoId: "" },
    };

    if (teamId) {
      const response = await axios.get(`/api/TeamPathway/${teamId}`);
      const pathwayId = (response.data as TeamPathwayProgress).teamPathway?.cmsPathwayId;
      currentPathways.teamPathway.id = pathwayId;

      if (pathwayId) {
        const cmsPathwayData = await cms.getById(CmsDocumentTypes.CmsTeamPathway, pathwayId);

        currentPathways.teamPathway.stepOneId =
          cmsPathwayData.data.pathway_steps[0].pathway_step.id;
        currentPathways.teamPathway.stepTwoId =
          cmsPathwayData.data.pathway_steps[1].pathway_step.id;
      }
    }

    const response = await axios.get<PersonalPathwayProgressApiModel>(
      "/api/personalpathway/current",
    );

    if (!response.data.currentPathway) {
      return currentPathways;
    }

    currentPathways.personalPathway.id = response.data.currentPathway?.cmsPersonalPathwayId;

    const cmsPathwayData = await cms.getById(
      CmsDocumentTypes.CmsPersonalPathway,
      currentPathways.personalPathway.id,
    );

    const stepOneId = cmsPathwayData.data.pathway_steps[0].pathway_step.id;
    const stepTwoId = cmsPathwayData.data.pathway_steps[1].pathway_step.id;

    currentPathways.personalPathway.stepOneId = stepOneId;
    currentPathways.personalPathway.stepTwoId = stepTwoId;

    return currentPathways;
  };

  const completeLevel = async (
    level: LevelItem,
    teamId: string | undefined,
  ): Promise<LevelCompleteResponse> => {
    if (!teamId) {
      return {
        status: ref<LevelCompleteStatus>("error"),
        errorMessage: ref<string>("No team id provided"),
      };
    }

    const errorMessage = ref("");
    const status = ref<LevelCompleteStatus>("pending");

    try {
      await axios.post("/api/teamlevels/CompleteLevel", {
        title: cms.asText(level.data.title),
        prismicId: level.id,
        prismicUid: level.uid,
        teamId,
        levelNumber: level.number,
      });
      status.value = "success";
    } catch (error) {
      errorMessage.value =
        (error as AxiosError).response?.data.message ?? "There was an error completing the level";
      status.value = "error";
    }

    return { status, errorMessage };
  };

  return {
    prepareLevelsAndMilestones,
    setLevelsStatus,
    castAsLevel,
    setLevelLink,
    completeLevel,
    initiatePathwayFromLevel,
    getCurrentPathwayIds,
  };
};

export default useTeamLeadLevels;
